import React from 'react';
import type { AdminCompanyDto, AdminLicenseDto, AdminUserDto } from '../types';
import {
  ADMIN_TENANT_USER_ROLES,
  ADMIN_USER_ROLES,
  ADMIN_USER_STATUSES,
  filterCompanyLicenses,
  formatAdminRelativeTime,
  type UserDraft,
  type UserDraftErrors,
} from '../lib/adminUsers';
import { getLicenseTypeLabel } from '../lib/adminLicenses';
import { StatusPill } from './components';

interface FieldProps {
  label: string;
  error?: string;
  children: (props: {
    id: string;
    'aria-describedby'?: string;
    'aria-invalid': boolean;
  }) => React.ReactNode;
}

const DrawerField: React.FC<FieldProps> = ({ label, error, children }) => {
  const id = React.useId();
  const errorId = `${id}-error`;
  return (
    <div className="admin-user-field">
      <label htmlFor={id}>{label}</label>
      {children({
        id,
        'aria-describedby': error ? errorId : undefined,
        'aria-invalid': Boolean(error),
      })}
      {error && <small id={errorId} className="admin-user-field__error">{error}</small>}
    </div>
  );
};

interface UserFormProps {
  mode: 'invite' | 'edit';
  user: AdminUserDto | null;
  companies: readonly AdminCompanyDto[];
  licenses: readonly AdminLicenseDto[];
  now: Date;
  draft: UserDraft;
  errors: UserDraftErrors;
  protectedSuper: boolean;
  disabled: boolean;
  setField: <Key extends keyof UserDraft>(key: Key, value: UserDraft[Key]) => void;
  changeCompany: (companyId: number) => void;
  toggleLicense: (licenseId: number) => void;
}

const titleCase = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

const licenseStatusTone = (status: AdminLicenseDto['status']) => status === 'active'
  ? 'success' as const
  : status === 'suspended' ? 'warning' as const : 'critical' as const;

const AssignedLicenses: React.FC<Pick<UserFormProps,
  'draft' | 'licenses' | 'errors' | 'toggleLicense' | 'disabled'>> = ({
  draft, licenses, errors, toggleLicense, disabled,
}) => {
  const owned = filterCompanyLicenses(licenses, draft.companyId);
  return (
    <fieldset className="admin-user-licenses" aria-describedby={errors.licenseIds ? 'user-license-error' : undefined}>
      <legend>Assigned licenses</legend>
      <p>Only licenses owned by the selected company can be assigned.</p>
      {owned.length === 0 ? (
        <div className="admin-user-licenses__empty">No company licenses available</div>
      ) : owned.map((license) => (
        <label className="admin-user-license" key={license.id}>
          <input
            type="checkbox"
            aria-label={`Assign ${license.activationKey}`}
            checked={draft.licenseIds.includes(license.id)}
            disabled={disabled}
            onChange={() => toggleLicense(license.id)}
          />
          <span className="admin-user-license__key">{license.activationKey}</span>
          <span className="admin-user-license__pills">
            <StatusPill tone="line">{getLicenseTypeLabel(license.type)}</StatusPill>
            <StatusPill tone={licenseStatusTone(license.status)}>
              {titleCase(license.status)}
            </StatusPill>
          </span>
        </label>
      ))}
      {errors.licenseIds && (
        <small id="user-license-error" className="admin-user-field__error">
          {errors.licenseIds}
        </small>
      )}
    </fieldset>
  );
};

export const UserForm: React.FC<UserFormProps> = (props) => {
  const roles = props.mode === 'invite' || props.user?.role !== 'Super'
    ? ADMIN_TENANT_USER_ROLES
    : ADMIN_USER_ROLES;
  const isPlatformSuper = props.mode === 'edit'
    && props.user?.role === 'Super'
    && props.user.companyId === null;
  return (
    <div className="admin-user-form">
      <DrawerField label="Full name" error={props.errors.username}>
        {(field) => <input {...field} disabled={props.disabled} value={props.draft.username} onChange={(event) => props.setField('username', event.target.value)} />}
      </DrawerField>
      <DrawerField label="Email" error={props.errors.email}>
        {(field) => <input {...field} disabled={props.disabled} type="email" value={props.draft.email} onChange={(event) => props.setField('email', event.target.value)} />}
      </DrawerField>
      <DrawerField label="Company" error={props.errors.companyId}>
        {(field) => (
          <select
            {...field}
            disabled={props.disabled}
            value={String(props.draft.companyId)}
            onChange={(event) => {
              const id = Number(event.target.value);
              props.changeCompany(id);
            }}
          >
            <option value="0" disabled={!isPlatformSuper}>
              {isPlatformSuper ? 'No company (platform)' : 'Choose company'}
            </option>
            {props.companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
          </select>
        )}
      </DrawerField>
      <div className="admin-user-form__split">
        <DrawerField label="Role" error={props.errors.role}>
          {(field) => (
            <select {...field} disabled={props.disabled || props.protectedSuper} value={props.draft.role} onChange={(event) => props.setField('role', event.target.value as UserDraft['role'])}>
              {roles.map((role) => <option key={role} value={role}>{role}</option>)}
            </select>
          )}
        </DrawerField>
        <DrawerField label="Status" error={props.errors.status}>
          {(field) => (
            <select
              {...field}
              disabled={props.disabled || props.mode === 'invite' || props.protectedSuper}
              value={props.draft.status}
              onChange={(event) => props.setField('status', event.target.value as UserDraft['status'])}
            >
              {ADMIN_USER_STATUSES.map((status) => <option key={status} value={status}>{titleCase(status)}</option>)}
            </select>
          )}
        </DrawerField>
      </div>
      <DrawerField label="Last active">
        {(field) => <input {...field} disabled={props.disabled} readOnly value={formatAdminRelativeTime(props.user?.lastActiveAt ?? null, props.now)} />}
      </DrawerField>
      <AssignedLicenses
        draft={props.draft}
        licenses={props.licenses}
        errors={props.errors}
        disabled={props.disabled}
        toggleLicense={props.toggleLicense}
      />
    </div>
  );
};
